import React, { useState } from "react";
import { ReactSelect, TooltipCustom } from "@services/ui-components";
import WriteOffService from "../../../../../services/WriteOffService/WriteOffService";
import Button from "react-bootstrap/Button";
import { ProductWriteOffInput } from "@me-team/host/main/graphql/types";
import {
    CustomProductFromTechMapInterface,
    ProductToWriteOffCustomInterface,
    ReactSelectInterface,
} from "../../../../../interfaces/interfaces";
import { useTranslation } from "react-i18next";

type ProductTableRowProps = {
    techMapId: number;
    technology: CustomProductFromTechMapInterface;
    changeProductsToWriteOff: (
        productToWriteOff: ProductWriteOffInput,
        changedField: keyof ProductWriteOffInput,
        isFromTechMap?: boolean,
        techMapId?: number
    ) => void;
    removeProductFromWriteOff: (productId: number, isProductFromTechMap: boolean, techMapId?: number) => void;
};

const ProductTableRow: React.FC<ProductTableRowProps> = ({
    techMapId,
    technology,
    changeProductsToWriteOff,
    removeProductFromWriteOff,
}) => {
    const { t: translate } = useTranslation();
    const [selectedStorage, setSelectedStorage] = useState<ReactSelectInterface>(
        technology.product.storage
            ? {
                  value: technology.product.storage.id,
                  label: technology.product.storage.name,
              }
            : null
    );
    const [selectedUnit, setSelectedUnit] = useState<ReactSelectInterface>({
        label: technology.relationsUnit.name,
        value: technology.relationsUnit.id,
    });
    const [selectedQty, setSelectedQty] = useState<string>(
        technology.requiredQuantity === null ? "" : String(technology.requiredQuantity)
    );
    const [writeOffProductInfo, setWriteOffProductInfo] = useState<ProductToWriteOffCustomInterface>({
        product: technology.product.id,
        storage: technology.product.storage ? technology.product.storage.id : null,
        quantity: technology.requiredQuantity,
        unit: technology.relationsUnit.id,
        techMapId: techMapId,
        isZeroAccept: technology.isZeroAccept,
    });

    const onSelectStorage = (storage: ReactSelectInterface) => {
        setSelectedStorage(storage);
        setWriteOffProductInfo((prevState) => {
            return {
                ...prevState,
                storage: storage.value,
            };
        });
        changeProductsToWriteOff({ ...writeOffProductInfo, storage: storage.value }, "storage", true, techMapId);
    };

    const onChangeQtyProductHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value: string = event.target.value;

        if (value.length > 1 && value.startsWith("0")) return;

        if (value.length > 9) return;

        if (!/^\d*$/.test(value)) return;

        const currentQty = value ? Number(value) : null;
        setSelectedQty(value);
        setWriteOffProductInfo((prevState) => ({
            ...prevState,
            quantity: currentQty,
        }));

        changeProductsToWriteOff({ ...writeOffProductInfo, quantity: currentQty }, "quantity", true, techMapId);
    };

    const onSelectUnit = (unit: ReactSelectInterface) => {
        setSelectedUnit(unit);
        setWriteOffProductInfo((prevState) => {
            return {
                ...prevState,
                unit: unit.value,
            };
        });
        changeProductsToWriteOff({ ...writeOffProductInfo, unit: unit.value }, "unit", true, techMapId);
    };

    const onRemoveProduct = () => {
        removeProductFromWriteOff(technology.product.id, true, techMapId);
    };

    return (
        <tr key={technology.id}>
            <td className="col-2 ps-4 py-4">{technology.product.name}</td>
            <td className="col-1 py-4"></td>
            <td className="col-3 py-4">
                <ReactSelect
                    id="write-off-product-storage-field"
                    value={selectedStorage}
                    options={WriteOffService.reactSelectStorage(technology.product.storages)}
                    placeholder={translate("Warehouse")}
                    disabled={technology.isZeroAccept}
                    onChange={onSelectStorage}
                    isSearchable={false}
                    hasError={!!technology.errors.storageError}
                />
                {technology.errors.storageError && (
                    <span className={"text-danger fs-7"}>{technology.errors.storageError}</span>
                )}
            </td>
            <td className="col-2 py-4">
                <input
                    id="writeOff-produt-qty-field"
                    type="text"
                    inputMode="numeric"
                    className={`form-control pe-4 ${technology.errors.qtyError ? "is-invalid" : ""}`}
                    placeholder={translate("quantity")}
                    value={selectedQty}
                    onChange={onChangeQtyProductHandler}
                    onKeyDown={(e) => {
                        if (e.key === "-" || e.key === "e") {
                            e.preventDefault();
                        }
                    }}
                />
                {technology.errors.qtyError && <span className={"text-danger fs-7"}>{technology.errors.qtyError}</span>}
            </td>
            <td className="col-2 py-4">
                <ReactSelect
                    id="write-off-product-unit-field"
                    value={selectedUnit}
                    options={WriteOffService.reactSelectUnits(technology.product.units)}
                    placeholder={""}
                    onChange={onSelectUnit}
                    isSearchable={false}
                />
            </td>
            <td className="col-1 py-4"></td>
            <td className="col-1 text-end pe-0 py-4">
                {!technology.isZeroAccept && (
                    <TooltipCustom placement="bottom" text={translate("Delete Product")}>
                        <Button className={"btn rounded border text-dark bg-white rounded-1"} onClick={onRemoveProduct}>
                            <i className="bi bi-trash3 d-flex fs-normal"></i>
                        </Button>
                    </TooltipCustom>
                )}
                {technology.isZeroAccept && (
                    <TooltipCustom
                        placement="bottom"
                        align={"center"}
                        text={translate("To cancel the item write‑off, set its quantity to 0")}
                    >
                        <Button variant={"secondary"} className={"border rounded-1"}>
                            <i className="bi bi-trash3 d-flex text-secondary fs-normal"></i>
                        </Button>
                    </TooltipCustom>
                )}
            </td>
        </tr>
    );
};

export default React.memo(ProductTableRow);
